// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A component for displaying individual asset action events.
 * This module provides a standardized way to display various
 * types of asset events in a timeline, including shipping
 * tracking and text properties. It handles formatting of
 * dates and different property types.
 *
 * Key features:
 * - Event type display
 * - Date formatting
 * - Property rendering
 * - Tracking integration
 * - Empty state handling
 */
import { Event } from '@/asset/api/types';
import { AssetTimelineEvent } from '@/asset/components/timeline-event';
import { ShippingCarrierTrackingLabel } from '@/components/shipping-carrier-tracking-label';
import { Typography } from '@mui/material';

/**
 * Formats an event's date for display.
 * Returns a localized date string if the event has occurred,
 * otherwise returns a placeholder message.
 *
 * @param event - The event containing an optional occurrence date
 * @returns A formatted date string or placeholder text
 */
function getEventDescription(event: Event) {
  if (event.occurredAt === null) {
    return 'Not reported';
  }

  return event.occurredAt.toLocaleString({
    dateStyle: 'medium',
  });
}

/**
 * Props for configuring the AssetActionEvent component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The event to display.
   * Contains type, date, and property information.
   */
  event: Event;
}

/**
 * A component for displaying an asset action event.
 * Renders an event in a timeline format, showing its type,
 * date, and any associated properties like tracking numbers
 * or text values. Handles different property types with
 * appropriate visualizations.
 *
 * Features:
 * - Event type display
 * - Date formatting
 * - Property rendering
 * - Tracking integration
 * - Empty state handling
 * - Timeline integration
 *
 * Usage example:
 * ```tsx
 * function AssetTimeline({ events }: Props) {
 *   return events.map(event => (
 *     <AssetActionEvent
 *       key={event.id}
 *       event={event}
 *     />
 *   ));
 * }
 * ```
 */
export function AssetActionEvent(props: Props) {
  // Format the event date for display
  const description = getEventDescription(props.event);

  return (
    <AssetTimelineEvent
      type="action"
      label={props.event.type.name}
      description={description}
    >
      {props.event.properties.map((property) => {
        // Handle carrier tracking properties
        if (property.type === 'CARRIER_TRACKING') {
          if (property.value === null) {
            return null;
          }

          return (
            <ShippingCarrierTrackingLabel
              key={property.id}
              carrier={property.value.carrier}
              trackingIdentifier={property.value.trackingNumber}
            />
          );
        }

        // Handle text properties
        if (property.type === 'TEXT') {
          return (
            <Typography key={property.id} variant="caption">
              {property.label}: {property.value}
            </Typography>
          );
        }
      })}
    </AssetTimelineEvent>
  );
}
